import type { CheckState } from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import type { SelectPanelContextValue } from '@/storybook/components/FormCollection/components/DropdownList/SelectPanelContext';
import { createContext, useContext, type ChangeEvent, type ReactNode } from 'react';
import type { TreeItemLevel } from './TreeItem.types';
import type { useTreeItemRowState } from './useTreeItemRowState';

export type TreeItemRowState = ReturnType<typeof useTreeItemRowState>;

export type TreeItemContextValue = TreeItemRowState & {
  label: string;
  labelContent?: ReactNode;
  tooltipText?: string;
  forceTooltip?: boolean;
  icon?: ReactNode;
  badge?: ReactNode;
  endContent?: ReactNode;
  showCheckbox: boolean;
  checked: CheckState;
  onCheck?: (e?: ChangeEvent<HTMLInputElement>) => void;
  hasChildren: boolean;
  isExpanded: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  level: TreeItemLevel;
  caretDataTestId?: string;
  checkboxDataTestId?: string;
};

const TreeItemContext = createContext<TreeItemContextValue | null>(null);

export function TreeItemContextProvider({ value, children }: { value: TreeItemContextValue; children: ReactNode }) {
  return <TreeItemContext.Provider value={value}>{children}</TreeItemContext.Provider>;
}

export function useTreeItemContext(): TreeItemContextValue {
  const ctx = useContext(TreeItemContext);
  if (ctx == null) {
    throw new Error('TreeItem compound parts must be used inside TreeItem.Root');
  }
  return ctx;
}

/**
 * Resolves the effective disabled state for a TreeItem row.
 *
 * | isDisabledProp | panel.disabled | panel.isLoading | result |
 * |----------------|----------------|-----------------|--------|
 * | true           | any            | any             | true   |
 * | false          | any            | true            | true   |
 * | false          | any            | false           | false  |
 * | undefined      | true           | any             | true   |
 * | undefined      | false          | true            | true   |
 * | undefined      | false          | false           | false  |
 */
export function resolveEffectiveDisabled(isDisabledProp: boolean | undefined, panel: SelectPanelContextValue): boolean {
  if (isDisabledProp === true) {
    return true;
  }
  if (isDisabledProp === false) {
    return panel.isLoading;
  }
  return panel.disabled || panel.isLoading;
}
